import type { Template } from "@/common/types/templates";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Link } from "@tanstack/react-router";
import { t } from "i18next";
import { Pencil, Trash2 } from "lucide-react";
import { useDeleteTemplate } from "@/hooks/templates/useDeleteTemplate";
import { useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { useTemplateStore } from "@/store/templates.store";
import { useAuthStore } from "@/store/auth.store";

export const TemplateItem = ({
  template,
}: {
  template: Template;
}): JSX.Element => {
  const { deleteTemplate, error, result, loading } = useDeleteTemplate();
  const { fetchAndSetTemplates } = useTemplateStore();
  const { userData } = useAuthStore();

  const handleDeleteItem = async (id: number): Promise<void> => {
    await deleteTemplate({ templateId: id });
    void fetchAndSetTemplates({ userId: userData!.id });
  };

  useEffect(() => {
    if (result != null && !loading) {
      if (result) {
        toast({
          variant: "default",
          title: t("templates.deletedToast.successful.title"),
          description: t("templates.deletedToast.successful.description"),
        });
      }
    }

    if (error && result != null && !loading) {
      toast({
        variant: "error",
        title: t("templates.deletedToast.error.title"),
        description: t("templates.deletedToast.error.description"),
      });
    }
  }, [result, loading, error]);

  return (
    <div className="flex items-center justify-between py-4 px-6">
      <div className="space-y-1">
        <h3 className="text-primary font-medium text-base">{template.name}</h3>
      </div>
      <div className="flex items-center space-x-6">
        <Link
          params={{ templateId: "" + template.id }}
          to="/template/$templateId"
        >
          <Pencil
            className="cursor-pointer text-grey-400"
            height={20}
            width={20}
          />
        </Link>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Trash2
              className="cursor-pointer text-error"
              height={20}
              width={20}
            />
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("templates.deleteTemplateAlert.headerTitle")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("templates.deleteTemplateAlert.deleteCTA")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/80"
                onClick={() => {
                  void handleDeleteItem(template.id);
                }}
              >
                {t("templates.deleteTemplateAlert.deleteButton")}
              </AlertDialogAction>
              <AlertDialogCancel>
                {t("templates.deleteTemplateAlert.cancelButton")}
              </AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
